import type { FetchHooks } from "ofetch";

/**
 * A response handler that forwards all Set-Cookie response headers from the SSR
 * context to the UI.
 *
 * This is required because 'Set-Cookie' headers returned from API calls made
 * during SSR are not sent to the user's browser. If a user's session expires
 * and the refresh the browser, SSR will occur. The session cookie has a
 * "browser session" lifetime, but the expiration is embedded in the cookie data
 * itself in the form of the Frontier API token expiration. Should the token
 * expire before the user's browser session ends, the user's session is still
 * considered invalid (see defineSecureEventHandler). Should that be the case,
 * API calls during SSR will fail and respond with Set-Cookie response headers
 * to clear the session cookie. The only way this can be done is via cookie
 * forwarding.
 */
export const useCookieForwardingOnResponse = (
  serverEvent: ReturnType<typeof useRequestEvent>,
) => {
  const onResponse: FetchHooks["onResponse"] = (ctx) => {
    if (isServer() && serverEvent) {
      for (const setCookie of ctx.response.headers.getSetCookie()) {
        serverEvent.node.res.setHeader("Set-Cookie", setCookie);
      }
    }
  };
  return onResponse;
};

/**
 * In the case an API returns a error handle it as follows:
 * If session timeout then logout and redirect to login.
 * If fatal error then replace the Vue app (`app/app.vue`) with the Nuxt error
 * page (`app/error.vue`).
 */
export const useOnResponseError = (
  serverEvent: ReturnType<typeof useRequestEvent>,
  nuxtApp: ReturnType<typeof useNuxtApp>,
) => {
  const onResponseError: FetchHooks["onResponseError"] = async (ctx) => {
    if (ctx.response.status === 401) {
      await nuxtApp.runWithContext(async () => {
        const route = useRoute();
        const to = `/login?reason=user-session-timeout&redirect=${route.fullPath}`;

        if (isServer() && serverEvent) {
          // if it's a server event, navigateTo won't work, so set the location
          // header of the response explicitly
          serverEvent.node.res.statusCode = 302;
          serverEvent.node.res.setHeader("Location", to);
        } else {
          // on the client, clear the user session (ensure cookie is cleared and removes state)
          // then redirect to login with error message
          await useAppSession().clear();
          return await navigateTo(to);
        }
      });
    } else if (ctx.response.status >= 500) {
      await nuxtApp.runWithContext(() => {
        showError(
          createError({
            status: ctx.response.status,
            statusText: ctx.response.statusText,
          }),
        );
      });
    }
  };
  return onResponseError;
};
